import React, { useState, useMemo } from 'react';
import { BarChart, Bar, Cell, AreaChart, Area, PieChart, Pie, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts';
import { AlertTriangle, Shield, TrendingUp, Zap, Activity, Globe, RefreshCw, Calendar, Eye, EyeOff } from 'lucide-react';
import { T, CHART_COLORS, fmtMoney, fmtNum, safeNum, fmtMonth, healthColor, healthLabel, calcRiskScore, calcClientGroup, getGroupConfig, TRADER_TYPES, TRADER_COLORS, TRADER_ICONS } from '../theme';
import { StatCard, Card, TabBar, DataTable, StatusBadge, HealthBar, CustomTooltip, CoBadge, GroupBadge } from '../components/UI';
import _ from 'lodash';

const MNAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const ABUSE_FLAGS = ["Scalper","HedgeTrader","NewsTrader","HighFrequencyTrader","HeavyTrader","GainerTrader","LongTrader","IP_Abuser"];

// Smart date formatter — handles ISO, Excel serial, timestamps, partial strings
function fmtDate(v) {
  if (!v || v === "—" || v === "NULL" || v === null) return "—";
  const s = String(v).trim();
  // Skip time-only values like "00:00.0" or pure numbers < 100
  if (/^\d{1,2}:\d{2}/.test(s)) return "—";
  // Excel serial date (number > 30000 = roughly year 1982+)
  if (typeof v === "number" && v > 30000) {
    const d = new Date((v - 25569) * 86400000);
    if (!isNaN(d.getTime())) return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  }
  // ISO string or parseable date
  const d = new Date(s);
  if (!isNaN(d.getTime()) && d.getFullYear() > 2000) return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  // Already formatted or unknown — return as-is if looks like a date, else "—"
  if (/\d{4}/.test(s) || /\d{1,2}[\/-]\d{1,2}/.test(s)) return s;
  return "—";
}

// Clickable login renderer
function LoginCell({ v, row, onClick }) {
  return (
    <span className="mono" onClick={() => onClick?.(row)} style={{ display: "flex", alignItems: "center", gap: 6, color: T.cyan, cursor: "pointer" }}>
      <CoBadge co={row._company} size={14} />{v}
    </span>
  );
}

// Clickable group badge
function GroupCell({ v, row, gcfg, onClick }) {
  const g = gcfg.groups[v];
  if (!g) return <span style={{ color: T.textMuted, fontSize: 11 }}>{v || "—"}</span>;
  return (
    <span onClick={() => onClick?.(row)} style={{ padding: "2px 8px", borderRadius: 4, background: (g.color || T.cyan) + "20", color: g.color || T.cyan, fontSize: 10, fontWeight: 700, cursor: "pointer" }}>{g.name}</span>
  );
}

// ── Exclude toggle ──
function ExcludeBtn({ on, toggle, label }) {
  return (
    <button onClick={toggle} style={{ display: "flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 6, border: `1px solid ${on ? T.amber : T.border}`, background: on ? T.amber + "15" : "transparent", color: on ? T.amber : T.textMuted, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>
      {on ? <EyeOff size={11} /> : <Eye size={11} />}
      {on ? `Excl. ${label}` : `Exclude ${label}`}
    </button>
  );
}

// ── Risk Pie with exclude ──
function RiskPie({ data, excludeSafe }) {
  const items = [
    { name: "Safe (0-15)", value: data.safe, color: "#22c55e" },
    { name: "Low (16-26)", value: data.low, color: "#06b6d4" },
    { name: "Elevated (27-38)", value: data.elevated, color: "#f59e0b" },
    { name: "High (39-50)", value: data.high, color: "#f97316" },
    { name: "Critical (51+)", value: data.critical, color: "#ef4444" },
  ];
  const pieData = (excludeSafe ? items.slice(1) : items).filter(d => d.value > 0);
  return (
    <ResponsiveContainer width="100%" height={260}>
      <PieChart>
        <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={45} paddingAngle={2}>
          {pieData.map((d, i) => <Cell key={i} fill={d.color} />)}
        </Pie>
        <Tooltip content={<CustomTooltip />} />
        <Legend wrapperStyle={{ fontSize: 10 }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

// ═══ MAIN ═══
export default function Analytics({ C, onClientClick, filterYear, setFilterYear, filterMonths, setFilterMonths }) {
  const [tab, setTab] = useState("overview");
  const [exSafe, setExSafe] = useState(false);
  const [exGroupTop, setExGroupTop] = useState(false);
  const [exFlagTop, setExFlagTop] = useState(false);
  const [exAcctTop, setExAcctTop] = useState(false);
  const [exCountryJo, setExCountryJo] = useState(true);
  const [exCountryRiskTop, setExCountryRiskTop] = useState(true);
  const [gcFilter, setGcFilter] = useState("all"); // all, escalation, deescalation
  const [gcCountry, setGcCountry] = useState("all");
  const [countryDnaFlag, setCountryDnaFlag] = useState(null); // selected flag for country DNA

  const gcfg = getGroupConfig();
  const hs = C.healthScores || [];

  const availYears = [...new Set((C.allMonths || []).map(m => String(m).slice(0, 4)))].sort();
  const availMonthNums = (C.allMonths || []).filter(m => filterYear === "all" || String(m).startsWith(filterYear)).map(m => parseInt(String(m).slice(4)));
  const toggleMonth = (num) => { if (filterMonths.includes(num)) setFilterMonths(filterMonths.filter(m => m !== num)); else setFilterMonths([...filterMonths, num].sort((a, b) => a - b)); };

  // ── Risk stats ──
  const riskStats = useMemo(() => {
    const critical = hs.filter(c => c.healthScore >= 51).length;
    const high = hs.filter(c => c.healthScore >= 39 && c.healthScore < 51).length;
    const elevated = hs.filter(c => c.healthScore >= 27 && c.healthScore < 39).length;
    const arbCfg = gcfg.arbitrage;
    const isArb = c => { const cds = (arbCfg.conditions || []).filter(x => x.enabled); if (!cds.length) return false; return cds.every(cd => { const v = safeNum(c[cd.column]); if (cd.op === "flag") return String(c[cd.column]) === cd.value; if (cd.op === ">=") return v >= cd.value; if (cd.op === "<") return v > 0 && v < cd.value; if (cd.op === ">") return v > cd.value; return false; }); };
    const arbCount = C.clients.filter(isArb).length;
    const groupOrder = gcfg.groupOrder || [];
    const escalations = (C.groupChanges || []).filter(g => groupOrder.indexOf(g._newGroup) < groupOrder.indexOf(g._prevGroup));
    const deescalations = (C.groupChanges || []).filter(g => groupOrder.indexOf(g._newGroup) > groupOrder.indexOf(g._prevGroup));
    const totalAbuse = C.clients.reduce((s, c) => s + ABUSE_FLAGS.filter(f => c[f] === f).length, 0);
    return { critical, high, elevated, arbCount, escalations, deescalations, totalAbuse };
  }, [hs, C.clients, C.groupChanges, gcfg]);

  // Filtered group changes
  const filteredGC = useMemo(() => {
    let gc = C.groupChanges || [];
    if (gcFilter === "escalation") gc = riskStats.escalations;
    else if (gcFilter === "deescalation") gc = riskStats.deescalations;
    if (gcCountry !== "all") gc = gc.filter(g => g.Country === gcCountry);
    return gc;
  }, [C.groupChanges, gcFilter, gcCountry, riskStats]);

  const gcCountries = useMemo(() => {
    const set = new Set((C.groupChanges || []).map(g => g.Country).filter(Boolean));
    return [...set].sort();
  }, [C.groupChanges]);

  // ── Group dist ──
  const groupData = useMemo(() => {
    const order = gcfg.groupOrder || Object.keys(gcfg.groups);
    return order.map(gk => { const g = gcfg.groups[gk]; if (!g) return null; return { name: g.name || gk, key: gk, count: C.groupDist?.[gk] || 0, color: g.color || T.cyan }; }).filter(Boolean).sort((a, b) => b.count - a.count);
  }, [C.groupDist, gcfg]);
  const groupTopLabel = groupData[0]?.name || "";

  // ── Risk by Account Type ──
  const riskByAcctType = useMemo(() => {
    const groups = _.groupBy(hs.filter(c => c.Account_Type && c.Account_Type !== "NULL"), "Account_Type");
    return Object.entries(groups).map(([type, cls]) => ({ name: type, clients: cls.length, risky: cls.filter(c => c.healthScore >= 39).length })).sort((a, b) => b.risky - a.risky);
  }, [hs]);
  const acctTopLabel = riskByAcctType[0]?.name || "";

  // ── Flag radar ──
  const flagData = useMemo(() => (C.radarData || []).map(d => ({ ...d })).sort((a, b) => b.count - a.count), [C.radarData]);
  const flagTopLabel = flagData[0]?.type || "";

  // ── Country data ──
  const countryRisk = useMemo(() => {
    const groups = _.groupBy(hs.filter(c => c.Country && c.Country !== "NULL"), "Country");
    return Object.entries(groups).map(([country, cls]) => {
      const row = {
        country, clients: cls.length,
        active: cls.filter(c => c.ClientStatus === "Active").length,
        volume: _.sumBy(cls, c => safeNum(c.Volume)),
        deposit: _.sumBy(cls, c => safeNum(c.Deposit)),
        equity: _.sumBy(cls, c => safeNum(c.Equity)),
        profit: _.sumBy(cls, c => safeNum(c.Profit)),
        risky: cls.filter(c => c.healthScore >= 39).length,
        critical: cls.filter(c => c.healthScore >= 51).length,
      };
      ABUSE_FLAGS.forEach(f => { row[f] = cls.filter(c => c[f] === f).length; });
      row.totalAbuse = ABUSE_FLAGS.reduce((s, f) => s + row[f], 0);
      return row;
    }).sort((a, b) => b.clients - a.clients);
  }, [hs]);

  // Top risky country (excl Jordan)
  const topRiskyCountry = useMemo(() => countryRisk.filter(c => c.country !== "Jordan").sort((a, b) => b.risky - a.risky)[0], [countryRisk]);
  // Top active country (excl Jordan)
  const topActiveCountry = useMemo(() => countryRisk.filter(c => c.country !== "Jordan").sort((a, b) => b.active - a.active)[0], [countryRisk]);

  // Country DNA: flag breakdown per country for selected flag
  const countryDnaData = useMemo(() => {
    if (!countryDnaFlag) return [];
    return countryRisk.filter(c => c[countryDnaFlag] > 0).sort((a, b) => b[countryDnaFlag] - a[countryDnaFlag]);
  }, [countryRisk, countryDnaFlag]);

  // Clients for selected flag in selected country
  const [dnaCountry, setDnaCountry] = useState(null);
  const dnaClients = useMemo(() => {
    if (!countryDnaFlag || !dnaCountry) return [];
    return hs.filter(c => c.Country === dnaCountry && c[countryDnaFlag] === countryDnaFlag);
  }, [hs, countryDnaFlag, dnaCountry]);

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "country", label: "Countries" },
    { id: "reactivation", label: "Reactivation & New", count: C.reactivated?.length || 0 },
  ];

  const DateFilterBar = () => (
    <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, padding: "14px 18px", marginBottom: 16, display: "flex", alignItems: "center", gap: 16 }}>
      <Calendar size={16} color={T.cyan} style={{ flexShrink: 0 }} />
      <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
        <button onClick={() => { setFilterYear("all"); setFilterMonths([]); }}
          style={{ padding: "6px 14px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, background: filterYear === "all" ? T.cyan : T.bg, color: filterYear === "all" ? T.bgCard : T.textMuted }}>All</button>
        {availYears.map(y => (
          <button key={y} onClick={() => { setFilterYear(y); setFilterMonths([]); }}
            style={{ padding: "6px 14px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, background: filterYear === y ? T.cyan : T.bg, color: filterYear === y ? T.bgCard : T.textMuted }}>{y}</button>
        ))}
      </div>
      <div style={{ width: 1, height: 28, background: T.border, flexShrink: 0 }} />
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", flex: 1 }}>
        {MNAMES.map((name, i) => {
          const num = i + 1, hasData = availMonthNums.includes(num), isSelected = filterMonths.length === 0 || filterMonths.includes(num);
          return (
            <button key={num} onClick={() => hasData && toggleMonth(num)} disabled={!hasData}
              style={{ padding: "6px 12px", borderRadius: 6, border: "none", cursor: hasData ? "pointer" : "default", fontSize: 11, fontWeight: 600,
                background: !hasData ? "transparent" : (filterMonths.length === 0 ? `${T.cyan}15` : (isSelected ? T.cyan : T.bg)),
                color: !hasData ? `${T.textMuted}40` : (filterMonths.length === 0 ? T.cyan : (isSelected ? T.bgCard : T.textMuted)),
                opacity: hasData ? 1 : 0.3 }}>{name}</button>
          );
        })}
      </div>
      {(filterYear !== "all" || filterMonths.length > 0) && (
        <button onClick={() => { setFilterYear("all"); setFilterMonths([]); }}
          style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${T.border}`, background: "transparent", color: T.textMuted, cursor: "pointer", fontSize: 10, fontWeight: 600, flexShrink: 0 }}>✕ Clear</button>
      )}
    </div>
  );

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 20, color: T.text }}>Risk Analytics</h1>
      <TabBar tabs={tabs} active={tab} onChange={setTab} />

      {/* ═══ OVERVIEW ═══ */}
      {tab === "overview" && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 12, marginBottom: 20 }}>
            <StatCard icon={<AlertTriangle size={18} />} label="Critical Risk" value={riskStats.critical} color="#ef4444" sub="score 51+" />
            <StatCard icon={<AlertTriangle size={18} />} label="High Risk" value={riskStats.high} color="#f97316" sub="score 39-50" />
            <StatCard icon={<Activity size={18} />} label="Elevated Risk" value={riskStats.elevated} color="#f59e0b" sub="score 27-38" />
            <StatCard icon={<Zap size={18} />} label="Arbitrage" value={riskStats.arbCount} color="#ff0055" />
            <StatCard icon={<TrendingUp size={18} />} label="Escalations" value={riskStats.escalations.length} color={T.red} sub={`${riskStats.deescalations.length} de-esc.`} />
            <StatCard icon={<Shield size={18} />} label="Abuse Flags" value={riskStats.totalAbuse} color={T.orange} sub="active flags" />
          </div>

          {/* Row 1: Risk Pie + Group Distribution */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
            <Card accent="#ef4444">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Risk Level Distribution</span>
                <ExcludeBtn on={exSafe} toggle={() => setExSafe(!exSafe)} label="Safe" />
              </div>
              <RiskPie data={C.healthDist} excludeSafe={exSafe} />
            </Card>
            <Card accent={T.cyan}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Group Distribution</span>
                {groupData.length > 1 && groupData[0].count > groupData[1].count * 2 && (
                  <ExcludeBtn on={exGroupTop} toggle={() => setExGroupTop(!exGroupTop)} label={groupTopLabel} />
                )}
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={exGroupTop ? groupData.slice(1) : groupData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                  <XAxis dataKey="name" tick={{ fill: T.textDim, fontSize: 9 }} interval={0} angle={-20} textAnchor="end" height={50} />
                  <YAxis tick={{ fill: T.textDim, fontSize: 10 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Clients" radius={[4, 4, 0, 0]}>
                    {(exGroupTop ? groupData.slice(1) : groupData).map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>

          {/* Row 2: Net Money Flow + Monthly Profit (FIXED red below 0) */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
            <Card accent={T.green}>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 12 }}>Net Money Flow</div>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={(C.monthlyStats || []).map(m => ({ ...m, net: m.deposit - m.withdraw }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                  <XAxis dataKey="month" tick={{ fontSize: 9, fill: T.textDim }} angle={-45} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 10, fill: T.textDim }} />
                  <Tooltip content={<CustomTooltip />} />
                  <ReferenceLine y={0} stroke={T.textMuted} strokeDasharray="3 3" />
                  <Bar dataKey="net" name="Net Flow" radius={[3, 3, 0, 0]}>
                    {(C.monthlyStats || []).map((m, i) => <Cell key={i} fill={(m.deposit - m.withdraw) >= 0 ? T.green : T.red} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card accent={T.purple}>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 12 }}>Monthly Profit / Loss</div>
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={(C.monthlyStats || []).map(m => ({ ...m, profitPos: (m.profit || 0) >= 0 ? m.profit : 0, profitNeg: (m.profit || 0) < 0 ? m.profit : 0 }))}>
                  <defs>
                    <linearGradient id="profPosG" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#22c55e" stopOpacity={0.4} /><stop offset="100%" stopColor="#22c55e" stopOpacity={0.02} /></linearGradient>
                    <linearGradient id="profNegG" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#ef4444" stopOpacity={0.02} /><stop offset="100%" stopColor="#ef4444" stopOpacity={0.4} /></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                  <XAxis dataKey="month" tick={{ fontSize: 9, fill: T.textDim }} angle={-45} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 10, fill: T.textDim }} />
                  <Tooltip content={<CustomTooltip />} />
                  <ReferenceLine y={0} stroke={T.textMuted} strokeDasharray="3 3" />
                  <Area type="monotone" dataKey="profitPos" stroke="#22c55e" fill="url(#profPosG)" name="Profit" strokeWidth={2} connectNulls />
                  <Area type="monotone" dataKey="profitNeg" stroke="#ef4444" fill="url(#profNegG)" name="Loss" strokeWidth={2} connectNulls />
                </AreaChart>
              </ResponsiveContainer>
            </Card>
          </div>

          {/* Row 3: Flag Radar + Risk by Account Type */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
            <Card accent={T.orange}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Trader Flag Distribution</span>
                {flagData.length > 1 && flagData[0].count > flagData[1].count * 2 && (
                  <ExcludeBtn on={exFlagTop} toggle={() => setExFlagTop(!exFlagTop)} label={flagTopLabel} />
                )}
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <RadarChart data={exFlagTop ? flagData.filter(d => d.type !== flagTopLabel) : flagData}>
                  <PolarGrid stroke={T.border} />
                  <PolarAngleAxis dataKey="type" tick={{ fill: T.textDim, fontSize: 9 }} />
                  <PolarRadiusAxis tick={{ fill: T.textMuted, fontSize: 8 }} />
                  <Radar dataKey="count" stroke={T.orange} fill={T.orange} fillOpacity={0.2} strokeWidth={2} />
                  <Tooltip content={<CustomTooltip />} />
                </RadarChart>
              </ResponsiveContainer>
            </Card>
            <Card accent={T.red}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Risky Clients by Account Type</span>
                {riskByAcctType.length > 1 && riskByAcctType[0].risky > riskByAcctType[1].risky * 2 && (
                  <ExcludeBtn on={exAcctTop} toggle={() => setExAcctTop(!exAcctTop)} label={acctTopLabel} />
                )}
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={(exAcctTop ? riskByAcctType.filter(d => d.name !== acctTopLabel) : riskByAcctType).slice(0, 10)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                  <XAxis type="number" tick={{ fontSize: 10, fill: T.textDim }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: T.textDim }} width={90} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="risky" name="Risky Clients (36+)" radius={[0, 4, 4, 0]} fill={T.red} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>

          {/* Row 4: Group Changes with clickable filter */}
          <Card accent={T.amber}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Group Changes This Period</span>
              {gcCountries.length > 0 && (
                <select value={gcCountry} onChange={e => setGcCountry(e.target.value)}
                  style={{ padding: "5px 10px", borderRadius: 6, border: `1px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 11, cursor: "pointer" }}>
                  <option value="all">All Countries</option>
                  {gcCountries.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              )}
            </div>
            {(C.groupChanges || []).length > 0 ? (
              <div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 16 }}>
                  {(() => {
                    const allGc = gcCountry === "all" ? (C.groupChanges || []) : (C.groupChanges || []).filter(g => g.Country === gcCountry);
                    const groupOrder = gcfg.groupOrder || [];
                    const esc = allGc.filter(g => groupOrder.indexOf(g._newGroup) < groupOrder.indexOf(g._prevGroup));
                    const deesc = allGc.filter(g => groupOrder.indexOf(g._newGroup) > groupOrder.indexOf(g._prevGroup));
                    return [
                      { key: "all", label: "Total Changes", value: allGc.length, bg: T.bg, color: T.text },
                      { key: "escalation", label: "Escalations ↑", value: esc.length, bg: T.redBg, color: T.red },
                      { key: "deescalation", label: "De-escalations ↓", value: deesc.length, bg: T.greenBg, color: T.green },
                    ].map(b => (
                      <div key={b.key} onClick={() => setGcFilter(b.key)}
                        style={{ background: gcFilter === b.key ? b.color + "20" : b.bg, border: `2px solid ${gcFilter === b.key ? b.color : "transparent"}`, borderRadius: 8, padding: 12, textAlign: "center", cursor: "pointer", transition: "all 0.2s" }}>
                        <div style={{ fontSize: 22, fontWeight: 800, color: b.color }}>{b.value}</div>
                        <div style={{ fontSize: 10, color: b.color }}>{b.label}</div>
                      </div>
                    ));
                  })()}
                </div>
                <DataTable exportTitle="Analytics-GroupChanges" pageSize={10} emptyMsg="No changes" columns={[
                  { key: "Login", label: "Login", render: (v, r) => <LoginCell v={v} row={r} onClick={onClientClick} /> },
                  { key: "Name", label: "Name", render: (v, r) => <span onClick={() => onClientClick?.(r)} style={{ cursor: "pointer", fontSize: 12 }}>{(v || "—").substring(0, 20)}</span> },
                  { key: "_prevGroup", label: "From", render: (v, r) => <GroupBadge group={v} size="sm" client={r} /> },
                  { key: "_newGroup", label: "To", render: (v, r) => <GroupBadge group={v} size="sm" client={r} /> },
                  { key: "_prevScore", label: "Prev", render: v => <span className="mono" style={{ color: healthColor(v), fontWeight: 600 }}>{v}</span> },
                  { key: "healthScore", label: "Now", render: v => <span className="mono" style={{ color: healthColor(v), fontWeight: 600 }}>{v}</span> },
                  { key: "Country", label: "Country", render: v => <span style={{ fontSize: 11, color: T.textDim }}>{v || "—"}</span> },
                ]} data={filteredGC} />
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: 30, color: T.textMuted, fontSize: 13 }}>No group changes detected. Try a different date filter.</div>
            )}
          </Card>
        </div>
      )}

      {/* ═══ COUNTRIES ═══ */}
      {tab === "country" && (
        <div>
          <DateFilterBar />

          {/* Cards - better info */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12, marginBottom: 20 }}>
            <StatCard icon={<Globe size={18} />} label="Countries" value={countryRisk.length} color={T.cyan} />
            <StatCard label="Jordan" value={countryRisk.find(c => c.country === "Jordan")?.clients || 0} color={T.amber} sub={`${countryRisk.find(c => c.country === "Jordan")?.risky || 0} risky`} />
            <StatCard label="Top Risky Country" value={topRiskyCountry?.country || "—"} color={T.red} sub={`${topRiskyCountry?.risky || 0} risky clients`} />
            <StatCard label="Top Active (non-JO)" value={topActiveCountry?.country || "—"} color={T.green} sub={`${topActiveCountry?.active || 0} active`} />
            <StatCard icon={<AlertTriangle size={18} />} label="Total Risky" value={countryRisk.reduce((s, c) => s + c.risky, 0)} color={T.red} sub="score 39+" />
          </div>

          {/* Charts */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
            <Card accent={T.red}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Risky Clients by Country</span>
                <ExcludeBtn on={exCountryJo} toggle={() => setExCountryJo(!exCountryJo)} label="Jordan" />
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={(exCountryJo ? countryRisk.filter(c => c.country !== "Jordan") : countryRisk).sort((a, b) => b.risky - a.risky).filter(c => c.risky > 0).slice(0, 12)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                  <XAxis type="number" tick={{ fontSize: 10, fill: T.textDim }} />
                  <YAxis type="category" dataKey="country" tick={{ fontSize: 10, fill: T.textDim }} width={90} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="risky" name="Risky (36+)" radius={[0, 4, 4, 0]} fill={T.red} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card accent={T.cyan}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Clients by Country</span>
                <ExcludeBtn on={exCountryRiskTop} toggle={() => setExCountryRiskTop(!exCountryRiskTop)} label="Jordan" />
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={(exCountryRiskTop ? countryRisk.filter(c => c.country !== "Jordan") : countryRisk).slice(0, 12)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                  <XAxis type="number" tick={{ fontSize: 10, fill: T.textDim }} />
                  <YAxis type="category" dataKey="country" tick={{ fontSize: 10, fill: T.textDim }} width={90} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="clients" name="Clients" radius={[0, 4, 4, 0]}>
                    {(exCountryRiskTop ? countryRisk.filter(c => c.country !== "Jordan") : countryRisk).slice(0, 12).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>

          {/* ── Abuse Flag DNA by Country ── */}
          <Card accent={T.orange} style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 12 }}>Abuse Flag DNA by Country</div>
            <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 12 }}>Select a flag to see which countries have flagged clients. Click a country row to view clients.</div>
            {/* Flag selector pills */}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
              {ABUSE_FLAGS.map(f => {
                const total = countryRisk.reduce((s, c) => s + (c[f] || 0), 0);
                const isActive = countryDnaFlag === f;
                return (
                  <button key={f} onClick={() => { setCountryDnaFlag(isActive ? null : f); setDnaCountry(null); }}
                    style={{ padding: "6px 14px", borderRadius: 8, border: `1px solid ${isActive ? (TRADER_COLORS[f] || T.orange) : T.border}`, background: isActive ? (TRADER_COLORS[f] || T.orange) + "20" : T.bg, color: isActive ? (TRADER_COLORS[f] || T.orange) : T.textMuted, fontSize: 11, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                    <span>{TRADER_ICONS[f] || "⚡"}</span> {f}
                    <span style={{ padding: "1px 6px", borderRadius: 4, background: total > 0 ? (TRADER_COLORS[f] || T.orange) + "30" : T.bg, fontSize: 9, fontWeight: 700 }}>{total}</span>
                  </button>
                );
              })}
            </div>

            {countryDnaFlag && (
              <div>
                {/* Country breakdown for selected flag */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: TRADER_COLORS[countryDnaFlag] || T.orange, marginBottom: 10 }}>
                      {TRADER_ICONS[countryDnaFlag] || "⚡"} {countryDnaFlag} — {countryRisk.reduce((s, c) => s + (c[countryDnaFlag] || 0), 0)} clients across {countryDnaData.length} countries
                    </div>
                    <div style={{ maxHeight: 300, overflowY: "auto" }}>
                      {countryDnaData.map(c => (
                        <div key={c.country} onClick={() => setDnaCountry(dnaCountry === c.country ? null : c.country)}
                          style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 6, marginBottom: 2, cursor: "pointer",
                            background: dnaCountry === c.country ? (TRADER_COLORS[countryDnaFlag] || T.orange) + "15" : "transparent",
                            border: `1px solid ${dnaCountry === c.country ? (TRADER_COLORS[countryDnaFlag] || T.orange) + "40" : "transparent"}` }}
                          onMouseEnter={e => { if (dnaCountry !== c.country) e.currentTarget.style.background = T.bgHover; }}
                          onMouseLeave={e => { if (dnaCountry !== c.country) e.currentTarget.style.background = "transparent"; }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: T.text, flex: 1 }}>{c.country}</span>
                          <span className="mono" style={{ fontSize: 13, fontWeight: 700, color: TRADER_COLORS[countryDnaFlag] || T.orange }}>{c[countryDnaFlag]}</span>
                          <span style={{ fontSize: 10, color: T.textMuted }}>of {c.clients}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Client list for selected country+flag */}
                  <div>
                    {dnaCountry ? (
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 10 }}>
                          {countryDnaFlag} clients in {dnaCountry} ({dnaClients.length})
                        </div>
                        <div style={{ maxHeight: 300, overflowY: "auto" }}>
                          {dnaClients.map(c => (
                            <div key={(c._company || "") + c.Login} onClick={() => onClientClick?.(c)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderRadius: 6, marginBottom: 2, background: T.bg, cursor: "pointer" }}
                              onMouseEnter={e => e.currentTarget.style.background = T.bgHover} onMouseLeave={e => e.currentTarget.style.background = T.bg}>
                              <CoBadge co={c._company} size={14} />
                              <span className="mono" style={{ color: T.cyan, fontWeight: 600, fontSize: 11 }}>#{c.Login}</span>
                              <span style={{ fontSize: 11, color: T.text, flex: 1 }}>{(c.Name || "—").substring(0, 22)}</span>
                              <span className="mono" style={{ fontSize: 11, color: healthColor(c.healthScore), fontWeight: 600 }}>{c.healthScore}</span>
                              <span style={{ fontSize: 10, color: T.textDim }}>{fmtMoney(safeNum(c.Volume))} vol</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: T.textMuted, fontSize: 12 }}>
                        ← Click a country to see {countryDnaFlag} clients
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </Card>

          {/* Country Table — abuse flag columns instead of generic "Flags" */}
          <Card>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 12 }}>All Countries</div>
            <DataTable exportTitle="Analytics-Countries" pageSize={25} emptyMsg="No data" columns={[
              { key: "country", label: "Country", render: v => <span style={{ fontWeight: 600, fontSize: 12 }}>{v}</span> },
              { key: "clients", label: "Clients", render: v => <span className="mono" style={{ fontWeight: 600 }}>{v}</span> },
              { key: "active", label: "Active", render: v => <span className="mono" style={{ color: T.green }}>{v}</span> },
              { key: "volume", label: "Volume", render: v => <span className="mono" style={{ color: T.cyan, fontSize: 11 }}>{fmtNum(v)}</span> },
              { key: "deposit", label: "Deposit", render: v => <span className="mono" style={{ color: T.amber, fontSize: 11 }}>{fmtMoney(v)}</span> },
              { key: "profit", label: "Profit", render: v => <span className="mono" style={{ color: v >= 0 ? T.green : T.red, fontSize: 11 }}>{fmtMoney(v)}</span> },
              { key: "risky", label: "Risky", render: v => <span className="mono" style={{ color: v > 0 ? T.red : T.textMuted, fontWeight: v > 0 ? 700 : 400 }}>{v}</span> },
              { key: "critical", label: "Crit.", render: v => <span className="mono" style={{ color: v > 0 ? "#ef4444" : T.textMuted, fontWeight: v > 0 ? 700 : 400 }}>{v}</span> },
              { key: "totalAbuse", label: "Abuse", render: v => <span className="mono" style={{ color: v > 0 ? T.orange : T.textMuted, fontWeight: v > 0 ? 700 : 400 }}>{v}</span> },
              ...ABUSE_FLAGS.map(f => ({
                key: f, label: (f === "HighFrequencyTrader" ? "HFT" : f === "IP_Abuser" ? "IP" : f.replace("Trader", "")),
                render: v => <span className="mono" style={{ color: v > 0 ? (TRADER_COLORS[f] || T.orange) : T.textMuted + "40", fontWeight: v > 0 ? 700 : 400, fontSize: 11 }}>{v || "—"}</span>
              })),
            ]} data={countryRisk} />
          </Card>
        </div>
      )}

      {/* ═══ REACTIVATION & NEW CLIENTS ═══ */}
      {tab === "reactivation" && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
            <StatCard icon={<RefreshCw size={18} />} label="Reactivated" value={C.reactivated?.length || 0} color={T.green} sub="Clients returned" />
            <StatCard label="Risky Reactivations" value={(C.reactivated || []).filter(c => c.healthScore >= 39).length} color={T.red} sub="score 39+" />
            <StatCard label="Total Volume" value={fmtNum((C.reactivated || []).reduce((s, c) => s + safeNum(c.Volume), 0))} color={T.blue} />
            <StatCard label="Total Equity" value={fmtMoney((C.reactivated || []).reduce((s, c) => s + safeNum(c.Equity), 0))} color={T.purple} />
          </div>
          <Card>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 12 }}>Reactivation & New Clients ({C.reactivated?.length || 0})</div>
            <DataTable exportTitle="Analytics-Reactivated" pageSize={15} emptyMsg="No reactivated clients" columns={[
              { key: "Login", label: "Login", render: (v, r) => <LoginCell v={v} row={r} onClick={onClientClick} /> },
              { key: "Name", label: "Name", render: (v, r) => <span onClick={() => onClientClick?.(r)} style={{ cursor: "pointer", fontSize: 12 }}>{(v || "—").substring(0, 22)}</span> },
              { key: "ClientStatus", label: "Status", render: v => <StatusBadge status={v} /> },
              { key: "PreviousClientStatus", label: "Prev Status", render: v => <span style={{ color: T.textDim, fontSize: 11 }}>{v || "—"}</span> },
              { key: "LastAccess", label: "Last Access", render: v => <span style={{ color: T.cyan, fontSize: 11 }}>{fmtDate(v)}</span> },
              { key: "Equity", label: "Equity", render: v => fmtMoney(safeNum(v)) },
              { key: "Volume", label: "Volume", render: v => fmtNum(safeNum(v), 1) },
              { key: "healthScore", label: "Risk", render: (v, r) => <HealthBar score={r.healthScore} /> },
              { key: "_clientGroup", label: "Group", render: (v, r) => <GroupBadge group={v} size="sm" client={r} /> },
              { key: "A.M", label: "A.M", render: v => <span style={{ fontSize: 11, color: T.textDim }}>{(v || "—").substring(0, 15)}</span> },
            ]} data={C.reactivated || []} />
          </Card>
        </div>
      )}
    </div>
  );
}
